import React, { useState, useEffect } from 'react';
import { ShieldCheck, ArrowLeft, RefreshCw } from 'lucide-react';

function reportSigTwoFactorDebug() {}

export default function TwoFactorForm({
  code,
  setCode,
  handleVerify,
  handleCancel,
  handleResend,
  loading,
  recipient,
  method = 'email'
}) {
  const isTotp = method === 'totp';
  const [timer, setTimer] = useState(30);
  const [canResend, setCanResend] = useState(false);

  useEffect(() => {
    if (isTotp) return;
    if (timer > 0) {
      const interval = setInterval(() => setTimer(t => t - 1), 1000);
      return () => clearInterval(interval);
    } else {
      setCanResend(true);
    }
  }, [timer, isTotp]);

  useEffect(() => {
    // Registra cuando el usuario ya tiene un codigo potencialmente valido para envio.
    if (String(code || '').length === 6) {
      reportSigTwoFactorDebug('TF-A', 'Codigo 2FA completo en UI', {
        codeLength: String(code || '').length,
        loading: !!loading,
      });
    }
  }, [code, loading]);

  const onResendClick = () => {
    if (!canResend) return;
    setTimer(30);
    setCanResend(false);
    // #region debug-point TF-C:resend-click
    reportSigTwoFactorDebug('TF-C', 'Usuario solicita reenvio de codigo', {
      canResend,
      loading: !!loading,
      timer,
    });
    // #endregion
    handleResend();
  };

  const handleSubmit = (e) => {
    // #region debug-point TF-B:form-submit
    reportSigTwoFactorDebug('TF-B', 'Submit del formulario 2FA desde UI', {
      codeLength: String(code || '').length,
      loading: !!loading,
      canResend,
      timer,
    });
    // #endregion
    handleVerify(e);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="text-center mb-6">
        <div className="mx-auto w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-3">
          <ShieldCheck className="h-6 w-6 text-blue-600" />
        </div>
        <h3 className="text-lg font-medium text-gray-900">Verificación de Dos Pasos</h3>
        <p className="text-sm text-gray-500 mt-1">
          {isTotp
            ? 'Abre tu app autenticadora (Google Authenticator u otra) e ingresa el código de 6 dígitos que muestra en este momento.'
            : 'Hemos enviado un código de verificación a tu correo electrónico. Por favor ingrésalo a continuación.'}
        </p>
        {!isTotp && recipient ? (
          <p className="text-xs text-gray-500 mt-2">
            Enviado a: <span className="font-semibold">{recipient}</span>
          </p>
        ) : null}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Código de Verificación
        </label>
        <input
          type="text"
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
          className="block w-full px-4 py-3 text-center text-2xl tracking-widest border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
          placeholder="000000"
          maxLength={6}
          autoFocus
          required
        />
      </div>

      <div className="flex flex-col space-y-3">
        <button
          type="submit"
          disabled={loading}
          className="w-full text-white py-3 px-4 rounded-lg font-semibold focus:outline-none focus:ring-2 focus:ring-offset-2 transform transition-all hover:scale-[1.02] active:scale-[0.98] shadow-lg hover:opacity-90 disabled:opacity-70 disabled:cursor-not-allowed"
          style={{ backgroundColor: '#776F74' }}
        >
          {loading ? 'Verificando...' : 'Verificar Código'}
        </button>

        {!isTotp && (
          <div className="text-center">
            <button
              type="button"
              onClick={onResendClick}
              disabled={!canResend || loading}
              className={`text-sm flex items-center justify-center w-full py-2 ${canResend ? 'text-blue-600 hover:text-blue-800' : 'text-gray-400 cursor-not-allowed'}`}
            >
              <RefreshCw className={`h-3 w-3 mr-1 ${!canResend && timer > 0 ? 'animate-spin' : ''}`} />
              {canResend ? 'Reenviar código' : `Reenviar en ${timer}s`}
            </button>
          </div>
        )}

        <button
          type="button"
          onClick={handleCancel}
          className="w-full flex items-center justify-center text-sm text-gray-600 hover:text-gray-900 py-2"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Volver al inicio de sesión
        </button>
      </div>
    </form>
  );
}
